import type {
  ActionFunctionArgs,
  ClientActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "react-router";
import { redirect, useLoaderData } from "react-router";
import { ConvertWorkbench } from "~/components/convert-workbench";
import { getOptionalUser } from "~/services/auth.server";
import { createRequestLogger } from "~/utils/logger";
import { loadLocalHistory } from "~/lib/history-store";
import { isMockMode } from "~/mocks/mockEnv.server";
import { mockHistory } from "~/mocks/mockData";

export const meta: MetaFunction = () => {
  return [
    { title: "Fluentry - Mixed Language to Fluent English" },
    {
      name: "description",
      content:
        "Transform mixed-language input into natural English with multiple style variations.",
    },
  ];
};

export async function loader({ request, context }: LoaderFunctionArgs) {
  const env = (context as any).cloudflare?.env || process.env || {};
  const logger = createRequestLogger({ scope: "routes._index.loader", env, request });

  if (isMockMode(env)) {
    return { history: mockHistory, user: null };
  }

  const user = await getOptionalUser(context, request);
  if (user) {
    return redirect("/app/convert");
  }

  logger.info("Serving trial mode for unauthenticated user");
  return { history: [], user: null };
}

export async function action(args: ActionFunctionArgs) {
  const { convertAction } = await import("~/services/convert-route.server");
  return convertAction(args);
}

export async function clientLoader() {
  const logger = createRequestLogger({ scope: "routes._index.clientLoader" });

  try {
    const localHistory = loadLocalHistory();
    const response = await fetch("/api/history");

    if (!response.ok) {
      throw new Error("API Error");
    }

    const data = (await response.json()) as {
      success?: boolean;
      history?: Array<Record<string, any>>;
      error?: { code: string; message: string };
    };
    const remoteHistory = data.success ? (data.history ?? []) : [];
    const merged = [...remoteHistory, ...localHistory].reduce((acc: any[], item: any) => {
      if (!acc.find((existing) => existing.id === item.id)) {
        acc.push(item);
      }
      return acc;
    }, []);

    return { history: merged, user: null };
  } catch (error) {
    logger.error("Client loader error", { err: error });
    return { history: loadLocalHistory(), user: null };
  }
}

export async function clientAction(args: ClientActionFunctionArgs) {
  const { convertClientAction } = await import("~/services/convert-route.client");
  return convertClientAction(args);
}

export default function IndexPage() {
  const { history, user } = useLoaderData<typeof loader>();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 px-4 py-8 sm:px-6 lg:px-8">
      <ConvertWorkbench
        history={history}
        user={user}
        showAuthHeader
        showExamples
        showHistory
        headerTitle="Fluentry Trial"
        headerDescription="Try instant conversion and PhoneticResolver suggestions before signing in"
      />
    </div>
  );
}
